import type {
  OrgDirectoryAdapter,
  OrgMember,
  OrgMemberSearchParams,
} from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';
import { getCurrentMemberUuid, getCurrentUser } from '@/utils/currentMember';

const normalize = (value: unknown) => String(value ?? '').trim();
const normalizePath = (value: string) => value.replace(/^\/+|\/+$/g, '');

const getMemberUrl = (params: OrgMemberSearchParams) => {
  const serviceUrl = normalize(BizAppConfig.appOpenServiceUrl).replace(
    /\/+$/,
    '',
  );
  const gatewayPath = normalizePath(BizAppConfig.appOpenApiGatewayPath);
  const categoryCode = normalizePath(
    BizAppConfig.appOrganizationAdminCategoryCode,
  );
  if (!serviceUrl || !gatewayPath || !categoryCode) {
    throw new Error('开放平台成员目录配置不完整');
  }
  const query = new URLSearchParams({
    pageNo: String(params.pageNo || 1),
    pageSize: String(params.pageSize || 100),
  });
  if (normalize(params.keyword))
    query.set('keyword', normalize(params.keyword));
  return `${serviceUrl}/${gatewayPath}/${categoryCode}/api/v1/members?${query}`;
};

const mapMember = (value: Record<string, unknown>): OrgMember | null => {
  const memberUuid = normalize(value.memberUuid) || normalize(value.id);
  if (!memberUuid) return null;
  return {
    memberUuid,
    name:
      normalize(value.name) ||
      normalize(value.nickname) ||
      normalize(value.username),
    nickname: normalize(value.nickname) || undefined,
    username: normalize(value.username) || undefined,
    phone: normalize(value.phone) || undefined,
    email: normalize(value.email) || undefined,
  };
};

const searchMembers = async (params: OrgMemberSearchParams) => {
  const currentUser = getCurrentUser();
  const memberUuid = getCurrentMemberUuid();
  if (!currentUser || !memberUuid) throw new Error('当前会话缺少成员信息');
  const { accessToken } = await fetchOpenPlatformToken(currentUser);
  const response = await fetch(getMemberUrl(params), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      memberUuid,
    },
  });
  if (!response.ok) throw new Error(`成员目录加载失败 (${response.status})`);
  const payload = (await response.json()) as {
    list?: Array<Record<string, unknown>>;
    total?: number;
    pageNo?: number;
    pageSize?: number;
  };
  return {
    list: (payload.list || []).map(mapMember).filter(Boolean) as OrgMember[],
    total: Number(payload.total || 0),
    pageNo: Number(payload.pageNo || params.pageNo || 1),
    pageSize: Number(payload.pageSize || params.pageSize || 100),
  };
};

export const orgDirectoryAdapter: OrgDirectoryAdapter = {
  getDepartmentTree: async () => [],
  searchMembers,
  getMemberById: async (memberUuid) => {
    const result = await searchMembers({
      keyword: memberUuid,
      pageNo: 1,
      pageSize: 100,
    });
    const member = result.list.find((item) => item.memberUuid === memberUuid);
    if (!member) throw new Error('未找到成员');
    return member;
  },
};
